import React, { useState, useMemo, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Chat from './Chat';
import MemoryGraph from './MemoryGraph';
import { RxAvatar } from 'react-icons/rx';
import { IoIosArrowForward } from 'react-icons/io';
import { IoSparkles, IoStop, IoChevronDown } from 'react-icons/io5';
import { HiOutlineChevronLeft, HiOutlineChevronRight, HiVolumeUp, HiVolumeOff } from 'react-icons/hi';
import { IoCloseOutline, IoSettingsOutline, IoCloudUploadOutline, IoTrashOutline } from 'react-icons/io5';
import { FiZoomIn, FiZoomOut, FiMaximize, FiMessageCircle } from 'react-icons/fi';
import { BiCollapseAlt, BiExpandAlt } from 'react-icons/bi';
import PhoneAccess from './PhoneAccess';
import CaptureSession from './CaptureSession';
import MemoryTimeline from './MemoryTimeline';
import MemoryConstellation from './MemoryConstellation';
import SoulView from './SoulView';
import { BsGraphUp } from 'react-icons/bs';
import { AiOutlineUnorderedList } from 'react-icons/ai';
import { TbCube } from 'react-icons/tb';
import { GiAura } from 'react-icons/gi';
import './MemoryChatView.css';

const CATEGORY_COLORS = {
  'experiences': '#3b725f',
  'preferences': '#c49a6c',
  'relationships': '#bd7b69',
  'memories': '#8b8b8b',
  'thoughts': '#9b8ec4',
  'ambient_capture': '#2D9CDB',
  'uncategorized': '#999',
};

function MemoryChatView({
  chat,
  onSendMessage,
  isLoading,
  token,
  api,
  onDeleteMessage,
  onPlayEmote,
  isMobile,
  assistantTalking,
  memories,
  highlightedMemories,
  onSwitchToAvatar,
  isWsOpen,
  chatLoading,
  selectedModel,
  onOpenModelSelection,
  onModelChange,
  isUpdatingModel,
  autoSpeak,
  onToggleAutoSpeak,
  isSpeaking,
  onStopSpeaking,
  speakVolume,
  onVolumeChange,
  speakSpeed,
  onSpeedChange,
  proactiveMode,
  onToggleProactive,
  proactiveStream,
  faceStatus,
  hasEnrolledFace,
  onEnrollFace,
  onClearFace,
  onOpenSettings,
  onOpenSoulOrb,
  onOpenLifeNarrative,
  onOpenAnalyticalPortrait,
  onOpenPrivacy,
  onOpenTemporalEvolution,
  onOpenFamilyTree,
  fetchMemories,
}) {
  const [enrolling, setEnrolling] = useState(false);
  const [enrollProgress, setEnrollProgress] = useState('');
  const [selectedRefMemory, setSelectedRefMemory] = useState(null);
  const [showReviewedBubbles, setShowReviewedBubbles] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  // Inline dropdown for the Legacy / Story mode selector (replaces the old
  // full-screen WavyPanel modal that opened on badge click).
  const [showModelMenu, setShowModelMenu] = useState(false);
  const modelBadgeRef = useRef(null);
  const modelMenuRef = useRef(null);
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [showCapture, setShowCapture] = useState(false);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [memoryView, setMemoryView] = useState(() => {
    const saved = localStorage.getItem('hfs_memory_view');
    return saved && ['graph', 'timeline', 'constellation', 'soul'].includes(saved) ? saved : 'soul';
  });
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
  const [sharedSearchQuery, setSharedSearchQuery] = useState('');
  const [sharedFilterCategory, setSharedFilterCategory] = useState(null);
  const [showSharedFilterDropdown, setShowSharedFilterDropdown] = useState(false);
  const mediaInputRef = useRef(null);
  const graphRef = useRef(null);
  const settingsRef = useRef(null);
  const settingsPopoverRef = useRef(null);
  const settingsBtnRef = useRef(null);
  const webcamVideoRef = useRef(null);

  // Persist tab selection to localStorage
  useEffect(() => {
    localStorage.setItem('hfs_memory_view', memoryView);
  }, [memoryView]);

  // Close settings popover on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inWrapper = settingsRef.current && settingsRef.current.contains(e.target);
      const inPopover = settingsPopoverRef.current && settingsPopoverRef.current.contains(e.target);
      if (!inWrapper && !inPopover) {
        setShowSettings(false);
      }
    };
    if (showSettings) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettings]);

  // Close model-mode dropdown on outside click
  useEffect(() => {
    if (!showModelMenu) return;
    const handleClickOutside = (e) => {
      const inBadge = modelBadgeRef.current && modelBadgeRef.current.contains(e.target);
      const inMenu = modelMenuRef.current && modelMenuRef.current.contains(e.target);
      if (!inBadge && !inMenu) setShowModelMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showModelMenu]);

  // Attach proactive webcam stream to video element
  useEffect(() => {
    if (webcamVideoRef.current && proactiveStream) {
      webcamVideoRef.current.srcObject = proactiveStream;
    }
  }, [proactiveStream]);

  // Get the latest referenced memories from the most recent assistant message
  const latestReferencedMemories = useMemo(() => {
    if (highlightedMemories && highlightedMemories.length > 0) {
      return highlightedMemories;
    }
    for (let i = chat.length - 1; i >= 0; i--) {
      if (chat[i].role === 'assistant' && chat[i].referenced_memories && chat[i].referenced_memories.length > 0) {
        return chat[i].referenced_memories;
      }
    }
    return [];
  }, [chat, highlightedMemories]);

  // Shared category counts for the header bar
  const sharedCategoryCounts = useMemo(() => {
    const counts = {};
    memories.forEach(m => {
      const cat = m.category || 'uncategorized';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count, color: CATEGORY_COLORS[name] || '#999' }))
      .sort((a, b) => b.count - a.count);
  }, [memories]);

  // Filtered memories for non-graph views
  const filteredMemories = useMemo(() => {
    let filtered = memories;
    if (sharedFilterCategory) {
      filtered = filtered.filter(m => (m.category || 'uncategorized') === sharedFilterCategory);
    }
    if (sharedSearchQuery) {
      const q = sharedSearchQuery.toLowerCase();
      filtered = filtered.filter(m =>
        (m.text && m.text.toLowerCase().includes(q)) ||
        (m.category && m.category.toLowerCase().includes(q))
      );
    }
    return filtered;
  }, [memories, sharedFilterCategory, sharedSearchQuery]);

  // Find related memories (memories from same category as selected)
  const relatedMemories = useMemo(() => {
    if (!selectedRefMemory) return [];
    return latestReferencedMemories
      .filter(m => m.id !== selectedRefMemory.id && m.category === selectedRefMemory.category)
      .slice(0, 3);
  }, [selectedRefMemory, latestReferencedMemories]);

  // Find linked memories (other referenced memories)
  const linkedMemories = useMemo(() => {
    if (!selectedRefMemory) return [];
    return latestReferencedMemories
      .filter(m => m.id !== selectedRefMemory.id)
      .slice(0, 3);
  }, [selectedRefMemory, latestReferencedMemories]);


  // Helper: enrich a partial memory with media fields from the full memories list
  const selectMemory = (mem) => {
    const full = memories.find(m => m.id === mem.id);
    setSelectedRefMemory({
      ...mem,
      media_url: full?.media_url || mem.media_url || null,
      media_type: full?.media_type || mem.media_type || null,
      thumbnail_url: full?.thumbnail_url || mem.thumbnail_url || null,
      media_items: full?.media_items || mem.media_items || [],
    });
  };

  // Auto-select top-scored memory when references arrive
  useEffect(() => {
    if (latestReferencedMemories.length > 0 && !selectedRefMemory) {
      const sorted = [...latestReferencedMemories].sort((a, b) => (b.score || 0) - (a.score || 0));
      selectMemory(sorted[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestReferencedMemories, selectedRefMemory]);

  // "Tell me more" sends a message about the selected memory
  const handleTellMeMore = () => {
    if (selectedRefMemory && onSendMessage) {
      onSendMessage(`Tell me more about this memory: "${selectedRefMemory.text.substring(0, 100)}"`);
    }
  };

  // Upload media to a memory (supports multiple files)
  const handleMediaUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !selectedRefMemory || !token || !api) return;
    setMediaUploading(true);
    try {
      const formData = new FormData();
      formData.append('memory_id', selectedRefMemory.id);
      for (let i = 0; i < files.length; i++) {
        formData.append('file', files[i]);
      }
      const res = await fetch(`${api}/upload_memory_media`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      // Normalize relative media URLs so they resolve against the backend
      const normalizeUrl = (url) => (url && url.startsWith('/media/')) ? api + url : url;
      // Update local selected memory with new gallery + display media
      setSelectedRefMemory(prev => ({
        ...prev,
        media_url: normalizeUrl(data.media_url),
        media_type: data.media_type,
        thumbnail_url: normalizeUrl(data.thumbnail_url),
        media_items: (data.media_items || []).map(item => ({
          ...item,
          media_url: normalizeUrl(item.media_url),
          thumbnail_url: normalizeUrl(item.thumbnail_url),
        })),
      }));
      // Refresh global memories list so graph re-renders
      if (fetchMemories) fetchMemories();
    } catch (err) {
      console.error('Media upload error:', err);
    } finally {
      setMediaUploading(false);
      if (mediaInputRef.current) mediaInputRef.current.value = '';
    }
  };

  // Remove a specific media item from a memory
  const handleMediaDelete = async (itemMediaUrl) => {
    if (!selectedRefMemory || !token || !api) return;
    setMediaUploading(true);
    try {
      const params = new URLSearchParams({ memory_id: selectedRefMemory.id });
      if (itemMediaUrl) params.append('media_url', itemMediaUrl);
      const res = await fetch(`${api}/delete_memory_media?${params.toString()}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      const data = await res.json();
      // Normalize relative media URLs so they resolve against the backend
      const normalizeUrl = (url) => (url && url.startsWith('/media/')) ? api + url : url;
      setSelectedRefMemory(prev => ({
        ...prev,
        media_url: normalizeUrl(data.media_url) || null,
        media_type: data.media_type || null,
        thumbnail_url: normalizeUrl(data.thumbnail_url) || null,
        media_items: (data.media_items || []).map(item => ({
          ...item,
          media_url: normalizeUrl(item.media_url),
          thumbnail_url: normalizeUrl(item.thumbnail_url),
        })),
      }));
      if (fetchMemories) fetchMemories();
    } catch (err) {
      console.error('Media delete error:', err);
    } finally {
      setMediaUploading(false);
    }
  };

  // Navigate to next/prev referenced memory
  const navigateMemory = (direction) => {
    if (!selectedRefMemory || latestReferencedMemories.length === 0) return;
    const currentIdx = latestReferencedMemories.findIndex(m => m.id === selectedRefMemory.id);
    const nextIdx = direction === 'next' 
      ? (currentIdx + 1) % latestReferencedMemories.length
      : (currentIdx - 1 + latestReferencedMemories.length) % latestReferencedMemories.length;
    const next = latestReferencedMemories[nextIdx];
    selectMemory(next);
  };

  return (
    <div className="memory-chat-view">
      {/* Left Panel: Chat */}
      {leftCollapsed ? (
        <div className="mcv-chat-panel collapsed">
          <button
            className="mcv-expand-strip"
            onClick={() => setLeftCollapsed(false)}
            title="Expand chat"
          >
            <HiOutlineChevronRight style={{ fontSize: 16 }} />
            <span className="mcv-expand-strip-label">Chat</span>
          </button>
        </div>
      ) : (
      <div className="mcv-chat-panel">
        <div className="mcv-chat-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <img
              src="https://ssobjects.blob.core.windows.net/logo/logo.png"
              alt="Heart Felt Souls Logo"
              style={{ height: isMobile ? 16 : 20, width: 'auto', objectFit: 'contain', cursor: 'pointer', flexShrink: 0 }}
              onClick={() => { window.location.href = '/'; }}
            />
            {selectedModel ? (
              <div className="mcv-model-selector">
                <button
                  ref={modelBadgeRef}
                  className={`mcv-model-badge ${showModelMenu ? 'open' : ''}`}
                  onClick={() => setShowModelMenu(v => !v)}
                  title={selectedModel === 'Synth' ? 'Story — Memory archivist' : 'Legacy — Your embodied digital twin'}
                  disabled={isUpdatingModel}
                >
                  <img
                    src={selectedModel === 'Synth' ? '/philosophical-icon.png' : '/fun-icon.png'}
                    alt={selectedModel === 'Synth' ? 'Story' : 'Legacy'}
                  />
                  {selectedModel === 'Synth' ? 'Story' : 'Legacy'}
                  <IoChevronDown className="mcv-model-badge-caret" style={{ fontSize: 12 }} />
                </button>
                {showModelMenu && ReactDOM.createPortal(
                  <div
                    ref={modelMenuRef}
                    className="mcv-model-menu"
                    style={modelBadgeRef.current ? (() => {
                      const r = modelBadgeRef.current.getBoundingClientRect();
                      return { top: r.bottom + 6, left: r.left };
                    })() : {}}
                  >
                    <div className="mcv-model-menu-label">Switch mode</div>
                    {[
                      { value: 'Echo', label: 'Legacy', desc: 'Your embodied digital twin', icon: '/fun-icon.png' },
                      { value: 'Synth', label: 'Story', desc: 'Memory archivist', icon: '/philosophical-icon.png' },
                    ].map(opt => {
                      const active = selectedModel === opt.value;
                      return (
                        <button
                          key={opt.value}
                          className={`mcv-model-option ${active ? 'active' : ''}`}
                          onClick={() => {
                            if (!active && onModelChange) onModelChange(opt.value);
                            setShowModelMenu(false);
                          }}
                          disabled={active || isUpdatingModel}
                        >
                          <img src={opt.icon} alt="" className="mcv-model-option-icon" />
                          <span className="mcv-model-option-text">
                            <span className="mcv-model-option-name">{opt.label}</span>
                            <span className="mcv-model-option-desc">{opt.desc}</span>
                          </span>
                          <span className={`mcv-model-option-radio ${active ? 'active' : ''}`} />
                        </button>
                      );
                    })}
                  </div>,
                  document.body
                )}
              </div>
            ) : (
              <h3 style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Memory</h3>
            )}
            {/* Mode toggle pill */}
            {onSwitchToAvatar && (
              <div className="mcv-mode-toggle">
                <div className="mcv-mode-toggle-active">Chat</div>
                <div className="mcv-mode-toggle-inactive" onClick={() => { setShowSettings(false); onSwitchToAvatar(); }}>Avatar</div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {isSpeaking && (
              <button className="mcv-stop-btn" onClick={onStopSpeaking} title="Stop speaking">
                <IoStop />
              </button>
            )}
            {/* Settings gear */}
            <div className="mcv-settings-wrapper" ref={settingsRef}>
              <button
                ref={settingsBtnRef}
                className={`mcv-settings-btn ${showSettings ? 'active' : ''}`}
                onClick={() => setShowSettings(!showSettings)}
                title="Settings"
              >
                <IoSettingsOutline />
              </button>
              {showSettings && ReactDOM.createPortal(
                <div
                  ref={settingsPopoverRef}
                  className="mcv-settings-popover"
                  style={settingsBtnRef.current ? (() => {
                    const r = settingsBtnRef.current.getBoundingClientRect();
                    return { top: r.bottom + 6, left: Math.max(8, r.right - 260) };
                  })() : {}}
                >
                  <div className="mcv-settings-title">Settings</div>

                  {/* ── Voice & Audio ───────────────────────────────────── */}
                  <div className="mcv-settings-section">
                    <div className="mcv-settings-section-label">Voice & Audio</div>
                    <div className="mcv-settings-row" onClick={onToggleAutoSpeak}>
                      <div className="mcv-settings-row-left">
                        {autoSpeak ? <HiVolumeUp style={{ fontSize: 14 }} /> : <HiVolumeOff style={{ fontSize: 14 }} />}
                        <span>AI Voice</span>
                      </div>
                      <div className={`mcv-settings-switch ${autoSpeak ? 'on' : ''}`}>
                        <div className="mcv-settings-switch-thumb" />
                      </div>
                    </div>
                    {autoSpeak && (
                      <div className="mcv-settings-sub">
                        <div className="mcv-volume-row">
                          <span className="mcv-volume-row-label">Vol</span>
                          <input
                            type="range" min="0" max="1" step="0.05"
                            value={speakVolume}
                            onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
                            className="mcv-volume-slider"
                          />
                          <span className="mcv-volume-label">{Math.round(speakVolume * 100)}%</span>
                        </div>
                        <div className="mcv-volume-row">
                          <span className="mcv-volume-row-label">Speed</span>
                          <div className="mcv-speed-options">
                            {[0.75, 1, 1.25, 1.5].map(s => (
                              <button
                                key={s}
                                className={`mcv-speed-btn ${speakSpeed === s ? 'active' : ''}`}
                                onClick={() => onSpeedChange(s)}
                              >
                                {s}x
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Presence ────────────────────────────────────────── */}
                  <div className="mcv-settings-section">
                    <div className="mcv-settings-section-label">Presence</div>
                    <div className="mcv-settings-row" onClick={onToggleProactive}>
                      <div className="mcv-settings-row-left">
                        <span className={`mcv-proactive-dot-sm ${proactiveMode ? 'active' : ''}`} />
                        <span>Proactive Mode</span>
                      </div>
                      <div className={`mcv-settings-switch ${proactiveMode ? 'on' : ''}`}>
                        <div className="mcv-settings-switch-thumb" />
                      </div>
                    </div>
                    {proactiveMode && (
                      <div className="mcv-settings-sub">
                        <div className="mcv-settings-subhead">Face Recognition</div>
                        {hasEnrolledFace ? (
                          <>
                            <div className="mcv-face-status">
                              <span className={`mcv-face-dot face-${faceStatus || 'no-one'}`} />
                              {faceStatus === 'recognized' ? 'You (recognized)' : faceStatus === 'stranger' ? 'Unknown face' : faceStatus === 'detected' ? 'Face detected' : 'No one detected'}
                            </div>
                            <button
                              className="mcv-face-btn danger"
                              onClick={(e) => { e.stopPropagation(); onClearFace && onClearFace(); }}
                            >Forget my face</button>
                          </>
                        ) : (
                          <>
                            <div className="mcv-face-help">
                              Register your face so Legacy can recognize you
                            </div>
                            <button
                              className="mcv-face-btn"
                              disabled={enrolling || !proactiveStream}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!onEnrollFace) return;
                                setEnrolling(true);
                                setEnrollProgress('Starting...');
                                const ok = await onEnrollFace((cur, total) => setEnrollProgress(`Capturing ${cur}/${total}...`));
                                setEnrollProgress(ok ? 'Done!' : 'Failed — try better lighting');
                                setEnrolling(false);
                                setTimeout(() => setEnrollProgress(''), 2000);
                              }}
                            >{enrolling ? enrollProgress : 'Register my face'}</button>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── Connections ─────────────────────────────────────── */}
                  <div className="mcv-settings-section">
                    <div className="mcv-settings-section-label">Connections</div>
                    <PhoneAccess token={token} api={api} />
                  </div>

                  {/* ── Advanced ────────────────────────────────────────── */}
                  {onOpenSettings && (
                    <div className="mcv-settings-section">
                      <div
                        className="mcv-settings-row"
                        onClick={() => { setShowSettings(false); onOpenSettings(); }}
                      >
                        <div className="mcv-settings-row-left">
                          <RxAvatar style={{ fontSize: 14 }} />
                          <span>All Settings</span>
                        </div>
                        <span style={{ fontSize: 12, opacity: 0.4 }}>›</span>
                      </div>
                    </div>
                  )}
                </div>,
                document.body
              )}
            </div>
            {/* Collapse left panel */}
            <button
              className="mcv-collapse-btn"
              onClick={() => setLeftCollapsed(!leftCollapsed)}
              title={leftCollapsed ? 'Expand chat' : 'Collapse chat'}
            >
              {leftCollapsed ? <BiExpandAlt /> : <BiCollapseAlt />}
            </button>
          </div>
        </div>
        <div className="mcv-chat-body">
          {chatLoading || !isWsOpen ? (
            <div className="mcv-connecting">
              <div className="mcv-connecting-dots">
                <span /><span /><span />
              </div>
              <span>Connecting</span>
            </div>
          ) : (
            <Chat
              chat={chat}
              onSendMessage={onSendMessage}
              isLoading={isLoading}
              token={token}
              api={api}
              onDeleteMessage={onDeleteMessage}
              onPlayEmote={onPlayEmote}
              isMobile={isMobile}
              assistantTalking={assistantTalking}
              selectedModel={selectedModel}
            />
          )}
        </div>
        {/* Speaking indicator bar */}
        {isSpeaking && (
          <div className="mcv-speaking-bar">
            <div className="mcv-speaking-waves">
              <span /><span /><span /><span /><span />
            </div>
            <span className="mcv-speaking-label">Speaking...</span>
          </div>
        )}
        {/* Typing indicator */}
        {isLoading && !isSpeaking && (
          <div className="mcv-typing-indicator">
            <span /><span /><span />
          </div>
        )}

        {/* Reviewed Bubbles Section - like reference image */}
        {latestReferencedMemories.length > 0 && (
          <div className="mcv-reviewed-section">
            <div className="mcv-reviewed-header" onClick={() => setShowReviewedBubbles(!showReviewedBubbles)}>
              <span>Reviewed Memories</span>
              <span className="mcv-reviewed-count">· {latestReferencedMemories.length}</span>
              <IoIosArrowForward style={{ 
                fontSize: 10, 
                transform: showReviewedBubbles ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }} />
            </div>
            {showReviewedBubbles && (
              <div className="mcv-reviewed-list">
                {latestReferencedMemories.map((mem, idx) => (
                  <div
                    key={mem.id || idx}
                    className="mcv-reviewed-item"
                    onClick={() => selectMemory(mem)}
                  >
                    <span 
                      className="mcv-reviewed-dot"
                      style={{ background: CATEGORY_COLORS[mem.category] || '#999' }}
                    />
                    <span className="mcv-reviewed-item-text">
                      {mem.text.substring(0, 60)}{mem.text.length > 60 ? '...' : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Center Panel: Bubble Cloud / Timeline */}
      <div className="mcv-graph-panel">
        {/* Hamburger Menu — top-right of graph */}
        <div className="mcv-hamburger-wrap">
          <button
            className={`mcv-hamburger-btn ${showHamburgerMenu ? 'active' : ''}`}
            onClick={() => setShowHamburgerMenu(prev => !prev)}
            title="Menu"
          >
            <span className="mcv-hamburger-line" />
            <span className="mcv-hamburger-line" />
            <span className="mcv-hamburger-line" />
          </button>
          {showHamburgerMenu && (
            <div className="mcv-hamburger-dropdown">
              <button className="mcv-hamburger-item" onClick={() => { setShowHamburgerMenu(false); onOpenSoulOrb && onOpenSoulOrb(); }}>
                <span>◉</span> Soul Profile
              </button>
              <button className="mcv-hamburger-item" onClick={() => { setShowHamburgerMenu(false); onOpenLifeNarrative && onOpenLifeNarrative(); }}>
                <span>📖</span> Life Narrative
              </button>
              <button className="mcv-hamburger-item" onClick={() => { setShowHamburgerMenu(false); onOpenAnalyticalPortrait && onOpenAnalyticalPortrait(); }}>
                <span>🔬</span> Analytical Portrait
              </button>
              <button className="mcv-hamburger-item" onClick={() => { setShowHamburgerMenu(false); onOpenPrivacy && onOpenPrivacy(); }}>
                <span>🛡️</span> Privacy
              </button>
              <button className="mcv-hamburger-item" onClick={() => { setShowHamburgerMenu(false); onOpenTemporalEvolution && onOpenTemporalEvolution(); }}>
                <span>📈</span> Soul Evolution
              </button>
              <button className="mcv-hamburger-item" onClick={() => { setShowHamburgerMenu(false); onOpenFamilyTree && onOpenFamilyTree(); }}>
                <span>👨‍👩‍👧‍👦</span> Family Tree
              </button>
            </div>
          )}
        </div>
        {/* View Toggle */}
        {memories.length > 0 && (
          <div className="mcv-view-toggle">
            <button
              className={`mcv-view-toggle-btn ${memoryView === 'soul' ? 'active' : ''}`}
              onClick={() => setMemoryView('soul')}
              title="Soul view"
            >
              <GiAura style={{ fontSize: 13 }} />
              <span>Soul</span>
            </button>
            <button
              className={`mcv-view-toggle-btn ${memoryView === 'graph' ? 'active' : ''}`}
              onClick={() => setMemoryView('graph')}
              title="Memories view"
            >
              <BsGraphUp style={{ fontSize: 13 }} />
              <span>Memories</span>
            </button>
            <button
              className={`mcv-view-toggle-btn ${memoryView === 'timeline' ? 'active' : ''}`}
              onClick={() => setMemoryView('timeline')}
              title="Timeline view"
            >
              <AiOutlineUnorderedList style={{ fontSize: 13 }} />
              <span>Timeline</span>
            </button>
            <button
              className={`mcv-view-toggle-btn ${memoryView === 'constellation' ? 'active' : ''}`}
              onClick={() => setMemoryView('constellation')}
              title="Spatial view"
            >
              <TbCube style={{ fontSize: 13 }} />
              <span>Spatial</span>
            </button>
          </div>
        )}
        <div className={`mcv-graph-container ${memoryView === 'timeline' ? 'mcv-graph-container-scroll' : ''} ${memoryView !== 'graph' && memories.length > 0 ? 'mcv-shared-view' : ''}`}>
          {/* Shared header bar for non-graph views — reuses MemoryGraph mg-* classes for identical layout */}
          {memories.length > 0 && memoryView !== 'graph' && (
            <>
              <div className="mg-top-bar">
                <div className="mg-total-count">
                  <span className="mg-total-value">{sharedFilterCategory || sharedSearchQuery ? filteredMemories.length : memories.length}</span>
                  <span className="mg-total-label">memories</span>
                </div>
                <div className="mg-filter-dropdown-wrap">
                  <button
                    className={`mg-filter-btn ${sharedFilterCategory ? 'has-filter' : ''}`}
                    onClick={() => setShowSharedFilterDropdown(prev => !prev)}
                  >
                    {sharedFilterCategory ? (
                      <>
                        <span className="mg-cat-dot" style={{ background: CATEGORY_COLORS[sharedFilterCategory] || '#999' }} />
                        {sharedFilterCategory}
                        <span
                          className="mg-filter-clear"
                          onClick={(e) => { e.stopPropagation(); setSharedFilterCategory(null); setShowSharedFilterDropdown(false); }}
                        >×</span>
                      </>
                    ) : (
                      <>Filter ▾</>
                    )}
                  </button>
                  {showSharedFilterDropdown && (
                    <div className="mg-filter-dropdown">
                      <button
                        className={`mg-filter-option ${sharedFilterCategory === null ? 'active' : ''}`}
                        onClick={() => { setSharedFilterCategory(null); setShowSharedFilterDropdown(false); }}
                      >
                        All categories
                      </button>
                      {sharedCategoryCounts.map(cat => (
                        <button
                          key={cat.name}
                          className={`mg-filter-option ${sharedFilterCategory === cat.name ? 'active' : ''}`}
                          onClick={() => { setSharedFilterCategory(cat.name); setShowSharedFilterDropdown(false); }}
                        >
                          <span className="mg-cat-dot" style={{ background: cat.color }} />
                          <span className="mg-filter-option-name">{cat.name}</span>
                          <span className="mg-filter-option-count">{cat.count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="mg-search-bar">
                <input
                  type="text"
                  className="mg-search-input"
                  placeholder="Search memories..."
                  value={sharedSearchQuery}
                  onChange={(e) => setSharedSearchQuery(e.target.value)}
                />
                {sharedSearchQuery && (
                  <span className="mg-search-info">
                    {filteredMemories.length} match{filteredMemories.length !== 1 ? 'es' : ''}
                    <button className="mg-search-clear" onClick={() => setSharedSearchQuery('')}>&times;</button>
                  </span>
                )}
              </div>
            </>
          )}
          {memories.length === 0 ? (
            <div className="mcv-graph-empty">
              <div className="mcv-graph-empty-orb" />
              <p className="mcv-graph-empty-title">Your story starts here</p>
              <p className="mcv-graph-empty-sub">
                Capture a conversation, a quiet thought, anything worth remembering.
                We turn what you share into memories that stay with you.
              </p>
              <button
                className="mcv-capture-fab mcv-capture-fab-empty"
                onClick={() => setShowCapture(true)}
              >
                <IoSparkles style={{ fontSize: 18 }} />
                <span>Capture your first memory</span>
              </button>
            </div>
          ) : memoryView === 'graph' ? (
            <MemoryGraph
              ref={graphRef}
              memories={memories}
              onMemoryClick={(memory) => setSelectedRefMemory(memory)}
              selectedMemory={selectedRefMemory}
              highlightedMemoryIds={latestReferencedMemories}
              isSpeaking={isSpeaking}
              compact
            />
          ) : memoryView === 'constellation' ? (
            <MemoryConstellation
              memories={filteredMemories}
              onMemoryClick={(memory) => setSelectedRefMemory(memory)}
              selectedMemory={selectedRefMemory}
              highlightedMemoryIds={latestReferencedMemories}
              isSpeaking={isSpeaking}
            />
          ) : memoryView === 'soul' ? (
            <SoulView
              token={token}
              api={api}
              isSpeaking={isSpeaking}
            />
          ) : (
            <MemoryTimeline
              memories={filteredMemories}
              onMemoryClick={(memory) => setSelectedRefMemory(memory)}
            />
          )}
        </div>
        {/* Floating Capture FAB — center bottom of graph */}
        {memories.length > 0 && (
          <button
            className="mcv-capture-fab"
            onClick={() => setShowCapture(true)}
            title="Guided Capture — preserve your memories with thoughtful prompts"
          >
            <IoSparkles style={{ fontSize: 15 }} />
            <span>Capture</span>
          </button>
        )}
        {memories.length > 0 && memoryView === 'graph' && (
          <div className="mcv-zoom-controls">
            <button
              className="mcv-zoom-btn"
              onClick={() => graphRef.current?.zoomIn()}
              title="Zoom in"
            >
              <FiZoomIn />
            </button>
            <button
              className="mcv-zoom-btn"
              onClick={() => graphRef.current?.zoomOut()}
              title="Zoom out"
            >
              <FiZoomOut />
            </button>
            <button
              className="mcv-zoom-btn"
              onClick={() => graphRef.current?.zoomToFit()}
              title="Fit to view"
            >
              <FiMaximize />
            </button>
          </div>
        )}
      </div>

      {/* Mobile speaking indicator */}
      {isMobile && isSpeaking && (
        <div className="mcv-mobile-speaking">
          <div className="mcv-speaking-waves">
            <span /><span /><span /><span /><span />
          </div>
        </div>
      )}

      {/* Right Panel: Detail */}
      {!isMobile && (
        <div className={`mcv-detail-panel ${rightCollapsed ? 'collapsed' : ''}`}>
          <div className="mcv-detail-header">
            {rightCollapsed ? (
              <button
                className="mcv-collapse-btn"
                onClick={() => setRightCollapsed(false)}
                title="Expand detail panel"
              >
                <BiExpandAlt />
              </button>
            ) : selectedRefMemory ? (
              <>
                {/* Left: collapse + prev/next navigation paired together so the
                    panel header doesn't try to fit three separate groups in
                    a 240-300px width (the old layout overlapped at tablet). */}
                <div className="mcv-detail-header-actions">
                  <button
                    className="mcv-collapse-btn"
                    onClick={() => setRightCollapsed(true)}
                    title="Collapse detail panel"
                  >
                    <BiCollapseAlt />
                  </button>
                  <button
                    className="mcv-detail-nav-btn"
                    onClick={() => navigateMemory('prev')}
                    title="Previous memory"
                  >
                    <HiOutlineChevronLeft />
                  </button>
                  <button
                    className="mcv-detail-nav-btn"
                    onClick={() => navigateMemory('next')}
                    title="Next memory"
                  >
                    <HiOutlineChevronRight />
                  </button>
                </div>
                {/* Right: close. The memory's category title now lives in the
                    body (mcv-detail-memory-title), so no header h3 needed. */}
                <button
                  className="mcv-detail-close-btn"
                  onClick={() => setSelectedRefMemory(null)}
                  title="Close memory"
                >
                  <IoCloseOutline />
                </button>
              </>
            ) : (
              <>
                <h3>Select a memory</h3>
                <button
                  className="mcv-collapse-btn"
                  onClick={() => setRightCollapsed(true)}
                  title="Collapse detail panel"
                >
                  <BiCollapseAlt />
                </button>
              </>
            )}
          </div>
          <div className="mcv-detail-body">
            {selectedRefMemory ? (
              <>
                {/* Memory Title / Category */}
                <div>
                  <h2 className="mcv-detail-memory-title">
                    {selectedRefMemory.category 
                      ? selectedRefMemory.category.charAt(0).toUpperCase() + selectedRefMemory.category.slice(1) 
                      : 'Memory'}
                  </h2>
                  <div className="mcv-detail-memory-meta">
                    <span className="mcv-episode-badge">
                      <span style={{ 
                        width: 6, height: 6, borderRadius: '50%', display: 'inline-block',
                        background: CATEGORY_COLORS[selectedRefMemory.category] || '#999' 
                      }} />
                      {selectedRefMemory.category || 'Memory'}
                    </span>
                    {selectedRefMemory.score && (
                      <span>{Math.round(selectedRefMemory.score * 100)}% match</span>
                    )}
                  </div>
                </div>

                {/* Privacy Tier (PRD 3.1) */}
                <div className="mcv-privacy-tier-row">
                  {[
                    { tier: 1, label: 'Public', color: '#4caf50' },
                    { tier: 2, label: 'Community', color: '#8bc34a' },
                    { tier: 3, label: 'Inner Circle', color: '#e8c77b' },
                    { tier: 4, label: 'Family', color: '#ff9800' },
                    { tier: 5, label: 'Vault', color: '#f44336' },
                  ].map(t => (
                    <button
                      key={t.tier}
                      className={`mcv-tier-btn${(selectedRefMemory.privacy_tier || 3) === t.tier ? ' active' : ''}`}
                      style={{
                        '--tier-color': t.color,
                      }}
                      title={t.label}
                      onClick={async () => {
                        try {
                          const res = await fetch(`${api}/privacy/tiers/memory/${selectedRefMemory.id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                            body: JSON.stringify({ tier: t.tier }),
                          });
                          if (res.ok) {
                            setSelectedRefMemory(prev => ({ ...prev, privacy_tier: t.tier }));
                            if (fetchMemories) fetchMemories();
                          }
                        } catch (e) { console.error('Tier update failed:', e); }
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* Content */}
                <div className="mcv-detail-memory-content">
                  {selectedRefMemory.text}
                </div>

                {/* Media Gallery / Upload */}
                <div className="mcv-media-section">
                  {(() => {
                    // Build effective media list: prefer media_items, fall back to top-level media_url
                    const items = (selectedRefMemory.media_items && selectedRefMemory.media_items.length > 0)
                      ? selectedRefMemory.media_items
                      : (selectedRefMemory.media_url
                        ? [{ media_url: selectedRefMemory.media_url, media_type: selectedRefMemory.media_type, thumbnail_url: selectedRefMemory.thumbnail_url }]
                        : []);
                    return items.length > 0 ? (
                      <div className="mcv-media-gallery">
                        {items.map((item, idx) => (
                          <div key={idx} className="mcv-media-gallery-item">
                            {item.media_type === 'video' ? (
                              <video
                                src={item.media_url}
                                controls
                                className="mcv-media-video"
                                poster={item.thumbnail_url || undefined}
                              />
                            ) : (
                              <img
                                src={item.thumbnail_url || item.media_url}
                                alt={`Media ${idx + 1}`}
                                className="mcv-media-image"
                                onClick={() => window.open(item.media_url, '_blank')}
                                style={{ cursor: 'pointer' }}
                              />
                            )}
                            <button
                              className="mcv-media-remove-btn"
                              onClick={() => handleMediaDelete(item.media_url)}
                              disabled={mediaUploading}
                              title="Remove this media"
                            >
                              <IoTrashOutline style={{ fontSize: 11 }} />
                            </button>
                          </div>
                        ))}
                        <button
                          className="mcv-media-add-more-btn"
                          onClick={() => mediaInputRef.current?.click()}
                          disabled={mediaUploading}
                          title="Add more media"
                        >
                          <IoCloudUploadOutline style={{ fontSize: 14 }} />
                          {mediaUploading ? '...' : '+'}
                        </button>
                      </div>
                    ) : (
                      <button
                        className="mcv-media-upload-btn"
                        onClick={() => mediaInputRef.current?.click()}
                        disabled={mediaUploading}
                      >
                        <IoCloudUploadOutline style={{ fontSize: 16 }} />
                        {mediaUploading ? 'Uploading...' : 'Attach Images or Videos'}
                      </button>
                    );
                  })()}
                  <input
                    ref={mediaInputRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleMediaUpload}
                  />
                </div>

                {/* Tell me more button */}
                <button className="mcv-tell-more-btn" onClick={handleTellMeMore}>
                  <FiMessageCircle style={{ fontSize: 13 }} />
                  Tell me more about this
                </button>

                {/* Linked Bubbles */}
                {linkedMemories.length > 0 && (
                  <div className="mcv-detail-section">
                    <div className="mcv-detail-section-title">
                      Linked Memories <span className="mcv-detail-section-count">{linkedMemories.length}</span>
                    </div>
                    {linkedMemories.map((mem, idx) => (
                      <div 
                        key={mem.id || idx} 
                        className="mcv-linked-item"
                        onClick={() => selectMemory(mem)}
                      >
                        <span 
                          className="mcv-linked-dot" 
                          style={{ background: CATEGORY_COLORS[mem.category] || '#999' }}
                        />
                        <div className="mcv-linked-info">
                          <span className="mcv-linked-title">
                            {mem.text.substring(0, 50)}{mem.text.length > 50 ? '...' : ''}
                          </span>
                          <span className="mcv-linked-date">
                            {Math.round((mem.score || 0) * 100)}% match
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Related Cloud */}
                {relatedMemories.length > 0 && (
                  <div className="mcv-detail-section">
                    <div className="mcv-detail-section-title">
                      Related Cloud <span className="mcv-detail-section-count">{relatedMemories.length}</span>
                    </div>
                    {relatedMemories.map((mem, idx) => (
                      <div 
                        key={mem.id || idx} 
                        className="mcv-linked-item"
                        onClick={() => selectMemory(mem)}
                      >
                        <span 
                          className="mcv-linked-dot" 
                          style={{ background: CATEGORY_COLORS[mem.category] || '#999' }}
                        />
                        <div className="mcv-linked-info">
                          <span className="mcv-linked-title">
                            {mem.text.substring(0, 50)}{mem.text.length > 50 ? '...' : ''}
                          </span>
                          <span className="mcv-linked-date">
                            {mem.category}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : latestReferencedMemories.length > 0 ? (
              <>
                <div className="mcv-memory-count">
                  <IoSparkles style={{ fontSize: 14, color: '#3b725f' }} />
                  <span>
                    <strong>{latestReferencedMemories.length}</strong> memories reviewed
                  </span>
                </div>
                {latestReferencedMemories.map((mem, idx) => (
                  <div
                    key={mem.id || idx}
                    className="mcv-ref-memory"
                    onClick={() => selectMemory(mem)}
                  >
                    <div className="mcv-ref-memory-header">
                      <span className="mcv-ref-memory-category">
                        {mem.category}
                      </span>
                      <span className="mcv-ref-memory-score">
                        {Math.round((mem.score || 0) * 100)}%
                      </span>
                    </div>
                    <div className="mcv-ref-memory-text">
                      {mem.text}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="mcv-detail-empty">
                <div className="mcv-detail-empty-icon">
                  <IoSparkles />
                </div>
                <p className="mcv-detail-empty-title">
                  Memories will surface here as you talk
                </p>
                <p className="mcv-detail-empty-sub">
                  When the AI draws on a memory, it lights up in the graph and appears in this panel — or tap any orb to inspect it yourself.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Webcam circle — visible when proactive mode is active and stream exists */}
      {proactiveMode && proactiveStream && (
        <div
          className={`mcv-webcam-circle mcv-face-${faceStatus || 'no-one'}`}
          title={faceStatus === 'recognized' ? 'You (recognized)' : faceStatus === 'stranger' ? 'Unknown face detected' : faceStatus === 'detected' ? 'Face detected' : 'Proactive mode active'}
        >
          <video
            ref={webcamVideoRef}
            autoPlay
            muted
            playsInline
          />
          <div className="mcv-webcam-ring" />
          <span className="mcv-webcam-label">
            {faceStatus === 'recognized' ? 'You' : faceStatus === 'stranger' ? '?' : faceStatus === 'detected' ? 'Face' : 'Live'}
          </span>
        </div>
      )}
      {/* Guided Capture Session */}
      <CaptureSession
        isOpen={showCapture}
        onClose={() => setShowCapture(false)}
        onSendMessage={onSendMessage}
        token={token}
        api={api}
      />
    </div>
  );
}

export default MemoryChatView;
